import { PushLocalState } from "interfaces/pwa";

export type PushOptInContext = {
	isStandalone: boolean;
	isSupported: boolean;
	isSubscribed: boolean;
	permission: NotificationPermission | "unsupported";
	nowMillis: number | null;
};

const REENABLE_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export const checkPushPopupState = (
	context: PushOptInContext,
	state: PushLocalState
): { nextState: PushLocalState; shouldShow: boolean } => {
	const { isStandalone, isSupported, isSubscribed, permission, nowMillis } =
		context;

	let nextState = state;
	let { everEnabled, disabledAt, neverShowPopup, initialPopupShown } =
		nextState;

	const enabledNow = isSubscribed;

	if (enabledNow) {
		if (!everEnabled || disabledAt != null) {
			nextState = {
				...nextState,
				everEnabled: true,
				disabledAt: null
			};
			({ everEnabled, disabledAt } = nextState);
		}
	} else {
		if (everEnabled && disabledAt == null) {
			const ts = nowMillis ?? Date.now();
			nextState = {
				...nextState,
				disabledAt: ts
			};
			({ disabledAt } = nextState);
		}
	}

	neverShowPopup = Boolean(neverShowPopup);
	everEnabled = Boolean(everEnabled);
	initialPopupShown = Boolean(initialPopupShown);
	const permissionEligible = everEnabled ? true : permission !== "denied";

	const baseEligible =
		isStandalone &&
		isSupported &&
		!enabledNow &&
		permissionEligible &&
		!neverShowPopup &&
		// For users who have never successfully enabled push, we only
		// auto-show the initial popup once. After that, we rely on
		// manual entry points (banner, settings, etc.).
		(!everEnabled ? !initialPopupShown : true);

	if (!baseEligible) {
		return { nextState, shouldShow: false };
	}

	if (!everEnabled) {
		// First-time auto opt-in: mark that we've shown the
		// initial prompt so we don't keep re-opening it on
		// every app launch (especially on iOS when permission
		// stays "default" even if the system is already blocked).
		nextState = {
			...nextState,
			initialPopupShown: true
		};
		return { nextState, shouldShow: true };
	}

	if (disabledAt != null && nowMillis != null) {
		const elapsed = nowMillis - disabledAt;
		if (elapsed >= REENABLE_INTERVAL_MS) {
			nextState = {
				...nextState,
				disabledAt: null
			};
			return { nextState, shouldShow: true };
		}
	}

	return { nextState, shouldShow: false };
};
